import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { StudentContext } from '../StudentContext';
import './ClassStudents.css';
import { updateStudentDateOfBirth, updateStudentStatus, updateChurchAttendance, updateCatechismAttendance } from '../GoogleSheetsService';
import { FaEdit } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const ClassStudents = () => {
  const { blockId, classId } = useParams();
  const navigate = useNavigate();
  const { studentsData, classMap, attendanceDates, catechismAttendanceDates, refreshData } = useContext(StudentContext);
  const [selectedChurchDate, setSelectedChurchDate] = useState(attendanceDates[attendanceDates.length - 1] || '');
  const [selectedCateshismDate, setSelectedCatechismDate] = useState(catechismAttendanceDates[catechismAttendanceDates.length - 1] || '');
  const [activeTab, setActiveTab] = useState('church');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editDateOfBirth, setEditDateOfBirth] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [modalMode, setModalMode] = useState('dob');
  const [localStudents, setLocalStudents] = useState([...(studentsData[classId] || [])]);
  const [selectedAttendanceDate, setSelectedAttendanceDate] = useState('');
  const [editAttendance, setEditAttendance] = useState('');

  const displayClass = classMap[classId] || 'Lớp Không Xác Định';
  const students = localStudents;

  const userRole = localStorage.getItem('userRole') || 'catechist';
  const catechistClassDisplayName = localStorage.getItem('catechistClassName');
  const blockLeader = localStorage.getItem('blockLeader');

  const blockDisplayNameToId = {
    'Khối Khai Tâm': 'khai-tam',
    'Khối Rước Lễ': 'ruoc-le',
    'Khối Thêm Sức': 'them-suc',
    'Khối Bao Đồng': 'bao-dong',
  };

  const canViewTranscript =
    userRole === 'admin' ||
    (userRole === 'catechist' && catechistClassDisplayName === displayClass) ||
    (userRole === 'catechist' && blockLeader && blockDisplayNameToId[blockLeader] === blockId);

  useEffect(() => {
    setLocalStudents([...(studentsData[classId] || [])]);
  }, [studentsData, classId]);

  const handleChurchDateChange = (e) => {
    setSelectedChurchDate(e.target.value);
  };

  const handleCatechismDateChange = (e) => {
    setSelectedCatechismDate(e.target.value);
  };

  const handleRefreshClick = () => {
    refreshData();
  };

  const handleStudentClick = (studentId) => {
    navigate(`/main/student/${studentId}`);
  };

  const handleTranscriptView = () => {
    if (canViewTranscript) {
      navigate(`/main/class-transcript/${blockId}/${classId}`);
    } else {
      alert('Bạn không có quyền xem bảng điểm cho lớp này.');
    }
  };

  const handleEditDob = (student) => {
    setSelectedStudent(student);
    setModalMode('dob');
    const dob = student.dateOfBirth || '';
    if (dob) {
      if (dob.includes('/')) {
        const [day, month, year] = dob.split('/');
        setEditDateOfBirth(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
      } else {
        setEditDateOfBirth(dob);
      }
    } else {
      setEditDateOfBirth('');
    }
    setIsModalOpen(true);
  };

  const handleEditStatus = (student) => {
    setSelectedStudent(student);
    setModalMode('status');
    setEditStatus(student.status || 'Còn đi học');
    setIsModalOpen(true);
  };

  const handleEditAttendance = (student, date, type) => {
    setSelectedStudent(student);
    setSelectedAttendanceDate(date);
    setModalMode(type === 'church' ? 'churchAttendance' : 'catechismAttendance');
    setEditAttendance(type === 'church' ? (student.attendance[date] || 'Chưa điểm danh') : (student.catechismAttendance[date] || 'Chưa điểm danh'));
    setIsModalOpen(true);
  };

  const handleSaveDob = async () => {
    try {
      const [year, month, day] = editDateOfBirth.split('-');
      const formattedDob = `${day}/${month}/${year}`;
      await updateStudentDateOfBirth(selectedStudent.id, formattedDob);
      alert('Cập nhật ngày sinh thành công!');
      setLocalStudents(prev =>
        prev.map(student =>
          student.id === selectedStudent.id ? { ...student, dateOfBirth: formattedDob } : student
        )
      );
      setIsModalOpen(false);
    } catch (error) {
      console.error('Lỗi khi cập nhật ngày sinh:', error);
      alert('Có lỗi xảy ra khi cập nhật ngày sinh');
    }
  };

  const handleSaveStatus = async () => {
    try {
      await updateStudentStatus(selectedStudent.id, editStatus);
      alert('Cập nhật trạng thái thành công!');
      setLocalStudents(prev =>
        prev.map(student =>
          student.id === selectedStudent.id ? { ...student, status: editStatus } : student
        )
      );
      setIsModalOpen(false);
    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      alert('Có lỗi xảy ra khi cập nhật trạng thái');
    }
  };

  const handleSaveAttendance = async () => {
    try {
      // Chuyển đổi selectedAttendanceDate sang định dạng yyyy-MM-dd
      let formattedDate = selectedAttendanceDate;
      if (selectedAttendanceDate.includes(' - ')) {
        const [, datePart] = selectedAttendanceDate.split(' - '); // Lấy phần "26/05/2025"
        const [day, month, year] = datePart.split('/');
        formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`; // Tạo "2025-05-26"
      }
  
      if (modalMode === 'churchAttendance') {
        await updateChurchAttendance(selectedStudent.id, formattedDate, editAttendance);
        setLocalStudents(prev =>
          prev.map(student =>
            student.id === selectedStudent.id
              ? { ...student, attendance: { ...student.attendance, [selectedAttendanceDate]: editAttendance } }
              : student
          )
        );
        alert('Cập nhật điểm danh đi lễ thành công!');
      } else if (modalMode === 'catechismAttendance') {
        await updateCatechismAttendance(selectedStudent.id, formattedDate, editAttendance);
        setLocalStudents(prev =>
          prev.map(student =>
            student.id === selectedStudent.id
              ? {
                  ...student,
                  catechismAttendance: { ...student.catechismAttendance, [selectedAttendanceDate]: editAttendance },
                }
              : student
          )
        );
        alert('Cập nhật điểm danh học giáo lý thành công!');
      }
      // Clear cache to refresh data
      localStorage.removeItem('studentsData');
      localStorage.removeItem('attendanceData');
      localStorage.removeItem('catechismAttendanceData');
      setIsModalOpen(false);
    } catch (error) {
      console.error('Lỗi khi cập nhật điểm danh:', error);
      alert('Có lỗi xảy ra khi cập nhật điểm danh');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudent(null);
    setEditDateOfBirth('');
    setEditStatus('');
    setEditAttendance('');
    setSelectedAttendanceDate('');
    setModalMode('dob');
  };

  const handleExportToExcel = () => {
    let data = [];
    let header = [];

    if (activeTab === 'church') {
      header = ['ID', 'Tên Thánh', 'Họ và Tên', ...displayedChurchDates.reverse()];
      data = students.map(student => [
        student.id,
        student.holyName,
        student.fullName,
        ...displayedChurchDates.reverse().map(date => student.attendance[date] || 'Chưa điểm danh'),
      ]);
    } else if (activeTab === 'catechism') {
      header = ['ID', 'Tên Thánh', 'Họ và Tên', ...displayedCatechismDates.reverse()];
      data = students.map(student => [
        student.id,
        student.holyName,
        student.fullName,
        ...displayedCatechismDates.reverse().map(date => student.catechismAttendance[date] || 'Chưa điểm danh'),
      ]);
    } else if (activeTab === 'profile') {
      header = ['ID', 'Tên Thánh', 'Họ và Tên', 'Ngày Sinh', 'Tình trạng học tập'];
      data = students.map(student => [
        student.id,
        student.holyName,
        student.fullName,
        formatDate(student.dateOfBirth),
        student.status || 'Còn đi học',
      ]);
    }

    const wb = XLSX.utils.book_new();
    const wsData = [header, ...data];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Danh sách học sinh');
    XLSX.writeFile(wb, `danh_sach_lop_${displayClass.replace(/ /g, '_')}.xlsx`);
  };

  useEffect(() => {
    if (attendanceDates.length > 0) {
      setSelectedChurchDate(attendanceDates[attendanceDates.length - 1]);
    }
    if (catechismAttendanceDates.length > 0) {
      setSelectedCatechismDate(catechismAttendanceDates[catechismAttendanceDates.length - 1]);
    }
  }, [attendanceDates, catechismAttendanceDates]);

  const maxDatesToShow = 7;
  const displayedChurchDates = attendanceDates.slice(-maxDatesToShow).reverse();
  const displayedCatechismDates = catechismAttendanceDates.slice(-maxDatesToShow).reverse();

  const formatDate = (dob) => {
    if (!dob) return 'Chưa có dữ liệu';
    if (dob.includes('/')) {
      const [day, month, year] = dob.split('/');
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    if (dob.includes('T')) {
      const date = new Date(dob);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return dob;
  };

  // Helper function to determine attendance button class and disabled state
  const getAttendanceClass = (status) => {
    let className = 'attendance-button';
    const trimmedStatus = (status || 'Chưa điểm danh').trim();
    const isDisabled = trimmedStatus.startsWith('Có lúc');
    if (trimmedStatus === 'Chưa điểm danh') {
      className += ' attendance-chua-diem-danh';
    } else if (trimmedStatus === 'Có xin phép') {
      className += ' attendance-co-xin-phep';
    } else if (isDisabled) {
      className += ' attendance-co-luc';
    }
    return { className, isDisabled };
  };

  return (
    <div className="class-content">
      <h3>Danh sách lớp: {displayClass}</h3>
      <div className="button-group">
        <button className="refresh-button" onClick={handleRefreshClick}>
          Làm mới dữ liệu
        </button>
        {canViewTranscript && (
          <button className="class-transcript-button" onClick={handleTranscriptView}>
            Xem bảng điểm
          </button>
        )}
        <button className="export-excel-button" onClick={handleExportToExcel}>
          Tải về file Excel
        </button>
      </div>

      <nav className="tabs-nav">
        <ul>
          <li>
            <button
              className={`nav-tab ${activeTab === 'church' ? 'active' : ''}`}
              onClick={() => setActiveTab('church')}
            >
              Điểm danh Đi Lễ
            </button>
          </li>
          <li>
            <button
              className={`nav-tab ${activeTab === 'catechism' ? 'active' : ''}`}
              onClick={() => setActiveTab('catechism')}
            >
              Điểm danh Học Giáo Lý
            </button>
          </li>
          <li>
            <button
              className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Sơ Yếu Lý Lịch
            </button>
          </li>
        </ul>
      </nav>

      {students.length > 0 ? (
        <div className="table-wrapper">
          {activeTab === 'church' && (
            <>
              <h4>Điểm danh Đi Lễ</h4>
              <table className="student-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tên Thánh</th>
                    <th>Họ và Tên</th>
                    <th className="mobile-only">
                      <select value={selectedChurchDate} onChange={handleChurchDateChange}>
                        {attendanceDates.slice().reverse().map((date, index) => (
                          <option key={index} value={date}>
                            {date}
                          </option>
                        ))}
                      </select>
                    </th>
                    {displayedChurchDates.reverse().map((date, index) => (
                      <th key={index} className="desktop-only">
                        {date}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={index}>
                      <td>{student.id}</td>
                      <td>{student.holyName}</td>
                      <td>
                        <button
                          className="student-name-link"
                          onClick={() => handleStudentClick(student.id)}
                        >
                          {student.fullName}
                        </button>
                      </td>
                      <td className="mobile-only">
                        <button
                          className={getAttendanceClass(student.attendance[selectedChurchDate]).className}
                          disabled={getAttendanceClass(student.attendance[selectedChurchDate]).isDisabled}
                          onClick={() => !getAttendanceClass(student.attendance[selectedChurchDate]).isDisabled && handleEditAttendance(student, selectedChurchDate, 'church')}
                        >
                          {student.attendance[selectedChurchDate] || 'Chưa điểm danh'}
                        </button>
                      </td>
                      {displayedChurchDates.map((date, index) => (
                        <td key={index} className="desktop-only">
                          <button
                            className={getAttendanceClass(student.attendance[date]).className}
                            disabled={getAttendanceClass(student.attendance[date]).isDisabled}
                            onClick={() => !getAttendanceClass(student.attendance[date]).isDisabled && handleEditAttendance(student, date, 'church')}
                          >
                            {student.attendance[date] || 'Chưa điểm danh'}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {activeTab === 'catechism' && (
            <>
              <h4>Điểm danh Học Giáo Lý</h4>
              <table className="student-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tên Thánh</th>
                    <th>Họ và Tên</th>
                    <th className="mobile-only">
                      <select value={selectedCateshismDate} onChange={handleCatechismDateChange}>
                        {catechismAttendanceDates.slice().reverse().map((date, index) => (
                          <option key={index} value={date}>
                            {date}
                          </option>
                        ))}
                      </select>
                    </th>
                    {displayedCatechismDates.reverse().map((date, index) => (
                      <th key={index} className="desktop-only">
                        {date}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={index}>
                      <td>{student.id}</td>
                      <td>{student.holyName}</td>
                      <td>
                        <button
                          className="student-name-link"
                          onClick={() => handleStudentClick(student.id)}
                        >
                          {student.fullName}
                        </button>
                      </td>
                      <td className="mobile-only">
                        <button
                          className={getAttendanceClass(student.catechismAttendance[selectedCateshismDate]).className}
                          disabled={getAttendanceClass(student.catechismAttendance[selectedCateshismDate]).isDisabled}
                          onClick={() => !getAttendanceClass(student.catechismAttendance[selectedCateshismDate]).isDisabled && handleEditAttendance(student, selectedCateshismDate, 'catechism')}
                        >
                          {student.catechismAttendance[selectedCateshismDate] || 'Chưa điểm danh'}
                        </button>
                      </td>
                      {displayedCatechismDates.map((date, index) => (
                        <td key={index} className="desktop-only">
                          <button
                            className={getAttendanceClass(student.catechismAttendance[date]).className}
                            disabled={getAttendanceClass(student.catechismAttendance[date]).isDisabled}
                            onClick={() => !getAttendanceClass(student.catechismAttendance[date]).isDisabled && handleEditAttendance(student, date, 'catechism')}
                          >
                            {student.catechismAttendance[date] || 'Chưa điểm danh'}
                          </button>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {activeTab === 'profile' && (
            <>
              <h4>Sơ Yếu Lý Lịch</h4>
              <table className="student-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Tên Thánh</th>
                    <th>Họ và Tên</th>
                    <th>Ngày Sinh</th>
                    <th>Tình trạng học tập</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => (
                    <tr key={index}>
                      <td>{student.id}</td>
                      <td>{student.holyName}</td>
                      <td>
                        <button
                          className="student-name-link"
                          onClick={() => handleStudentClick(student.id)}
                        >
                          {student.fullName}
                        </button>
                      </td>
                      <td>
                        <div className="dob-display">
                          <span>{formatDate(student.dateOfBirth)}</span>
                          <FaEdit
                            className="edit-icon"
                            onClick={() => handleEditDob(student)}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="status-display">
                          <span>{student.status || 'Còn đi học'}</span>
                          <FaEdit
                            className="edit-icon"
                            onClick={() => handleEditStatus(student)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      ) : (
        <p>Chưa có dữ liệu cho lớp {displayClass}.</p>
      )}

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            {selectedStudent && (
              <div className="student-info">
                <p><strong>ID:</strong> {selectedStudent.id}</p>
                <p><strong>Tên Thánh:</strong> {selectedStudent.holyName || 'Chưa có'}</p>
                <p><strong>Họ và Tên:</strong> {selectedStudent.fullName}</p>
              </div>
            )}
            <h3>
              {modalMode === 'dob'
                ? 'Chỉnh sửa ngày sinh'
                : modalMode === 'status'
                ? 'Chỉnh sửa tình trạng học tập'
                : modalMode === 'churchAttendance'
                ? 'Chỉnh sửa điểm danh đi lễ'
                : 'Chỉnh sửa điểm danh học giáo lý'}
            </h3>
            <div className="modal-body">
              {modalMode === 'dob' ? (
                <>
                  <label>
                    Ngày sinh:
                    <input
                      type="date"
                      value={editDateOfBirth}
                      onChange={(e) => setEditDateOfBirth(e.target.value)}
                    />
                  </label>
                  <p className="date-format-note">
                    <strong>Lưu ý:</strong><br />
                    - Nếu giao diện hiển thị <b>dd/mm/yyyy</b> có nghĩa là <b>ngày/tháng/năm.</b><br />
                    - Nếu giao diện hiển thị <b>mm/dd/yyyy</b> có nghĩa là <b>tháng/ngày/năm.</b>
                  </p>
                </>
              ) : modalMode === 'status' ? (
                <div className="status-controls">
                  <label>
                    <input
                      type="radio"
                      name="status"
                      value="Còn đi học"
                      checked={editStatus === 'Còn đi học'}
                      onChange={(e) => setEditStatus(e.target.value)}
                    />
                    Còn đi học
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="status"
                      value="Nghỉ luôn"
                      checked={editStatus === 'Nghỉ luôn'}
                      onChange={(e) => setEditStatus(e.target.value)}
                    />
                    Nghỉ luôn
                  </label>
                </div>
              ) : (
                <div className="attendance-controls modal-body-option">
                  <label>
                    <input
                      type="radio"
                      name="attendance"
                      value="Chưa điểm danh"
                      checked={editAttendance === 'Chưa điểm danh'}
                      onChange={(e) => setEditAttendance(e.target.value)}
                    />
                    Chưa điểm danh
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="attendance"
                      value="Có xin phép"
                      checked={editAttendance === 'Có xin phép'}
                      onChange={(e) => setEditAttendance(e.target.value)}
                    />
                    Có xin phép
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="attendance"
                      value="Không có thẻ"
                      checked={editAttendance === 'Không có thẻ'}
                      onChange={(e) => setEditAttendance(e.target.value)}
                    />
                    Không có thẻ
                  </label>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="save-button"
                onClick={
                  modalMode === 'dob'
                    ? handleSaveDob
                    : modalMode === 'status'
                    ? handleSaveStatus
                    : handleSaveAttendance
                }
              >
                Lưu
              </button>
              <button className="cancel-button" onClick={handleCloseModal}>
                Hủy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassStudents;